import styled from 'styled-components';

// A horizontal strip of per-type range lanes rendered ABOVE the trend plot (a
// separate timeline, not overlaid on the data). Width matches the plot area so
// each range pill lines up with the data below it.
export const BandOuter = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height}px;
  /* Bottom margin reserves the point-flag row: the trend chart draws its flags
     just above the plot (negative y, via the SVG's visible overflow), and they
     render in this gap — below the lanes, above the data line. */
  margin: 0 0 30px;
  pointer-events: none; /* children re-enable where interactive */
  /* Contain internal z-indexes (lane labels/badges go up to z-index 17) so they
     don't compete with chart-header controls like the calendar dropdown, which
     sit above this band in the DOM but stack at a low z-index. */
  isolation: isolate;
`;

// Thin neutral baseline running the length of a lane, behind the range pills.
export const LaneBaseline = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left || '0'};
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  background: #eef0f2;
  pointer-events: none;
  z-index: 1;
`;

// Per-type label for a lane in the standalone band. It sits on its own strip
// INSIDE the band, directly above the lane's pills (top-anchored at the strip's
// top, left-aligned to the band's 0%). Keeping it inside the band — rather than
// the previous `right: 100%` position in the plot's y-axis gutter — means the
// narrow, `overflow:auto`-clipped ancestor gutter can no longer clip the label
// (dot + first letter). Because it's on its own vertical strip above the pills,
// it never overlaps a range that starts at ~0%, and the pill track still maps
// 1:1 to [0..100%] aligned with the plot below.
export const BandLaneLabel = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: 0;
  display: inline-flex;
  align-items: center;
  height: 15px;
  padding: 0 8px 0 6px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${props => props.color};
  white-space: nowrap;
  pointer-events: none;
  z-index: 16;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    margin-right: 6px;
    border-radius: 50%;
    background: ${props => props.color};
  }
`;

// Shown in place of a boundary tick on a side where the range runs past the
// visible window. Without it a clamped pill is indistinguishable from one that
// genuinely starts or ends at the chart edge. The chevron points outward and a
// short fade bleeds the pill's colour off the edge, so "there is more of this
// range out there" reads at a glance.
//
// `pointer-events: none` is deliberate -- the pill underneath owns hover, and
// letting a 12px strip at each end swallow it would make the range's own
// tooltip flicker. That rules out a native `title`, so the meaning is carried
// for assistive tech by role="img" + aria-label on the element instead.
export const RangeContinues = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  height: ${props => props.height};
  width: 12px;
  /* Anchor to the pill's own edge: the left marker sits just inside the start,
     the right marker is pulled back inside the end. */
  transform: ${props => (props.side === 'left' ? 'none' : 'translateX(-100%)')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  background: linear-gradient(
    to ${props => (props.side === 'left' ? 'right' : 'left')},
    ${props => props.color}59,
    transparent
  );
  pointer-events: none;
  /* Must sit ABOVE the pill it annotates: RangeBar is z-index 13, rising to 15
     while hovered (AnnotationOverlay.styled.js). At a lower index the chevron is
     washed out by the pill at rest and disappears entirely on hover -- exactly
     when the user is inspecting the range. 16 clears the hovered pill; the
     overlap badge stays above at 17 so it remains the topmost hit target. */
  z-index: 16;
`;

// Boundary tick at a range's start (solid) or end (dashed), echoing the design.
export const RangeEdge = styled.div`
  position: absolute;
  top: ${props => props.top};
  left: ${props => props.left};
  height: ${props => props.height};
  border-left: ${props => (props.dashed ? '1px dashed' : '1.4px solid')} ${props => props.color};
  opacity: ${props => (props.dashed ? 0.5 : 0.85)};
  pointer-events: none;
  z-index: 2;
`;
